<template>
  <template v-if="data?.data">
    <v-text-field
      v-model="newItem"
      placeholder="What needs to be done?"
      @keyup.enter="createNewItem"
    />
    <v-list lines="two">
      <Item
        v-for="issue in data.data"
        :key="issue.number ?? 0"
        :auth
        :issue="issue"
        :repo
        @change-state="
          (state: 'open' | 'closed') =>
            changeIssue(issue.number ?? 0, { state })
        "
        @change-title="
          (title: string) => changeIssue(issue.number ?? 0, { title })
        "
      />
    </v-list>
    <v-row align="center" justify="space-between">
      <v-col cols="auto">
        <span v-if="openCount !== undefined">{{ openCount }} items left</span>
      </v-col>
      <v-col cols="auto">
        <v-btn-toggle v-model="stateSelection" mandatory>
          <v-btn value="all">All</v-btn>
          <v-btn value="open">Active</v-btn>
          <v-btn value="closed">Completed</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
  </template>
</template>
<script setup lang="ts">
import { computed, type ComputedRef, type Ref, ref } from "vue";
import {
  type EditIssueOption,
  type IssueListIssuesParams,
  useIssueCreateIssue,
  useIssueEditIssue,
  useIssueListIssues,
  useRepoGet,
} from "../gen/forgejo-vue";
import type { AuthorizationToken } from "@/authenticationTokens";
import { useQueryClient } from "@tanstack/vue-query";
const props = defineProps<{
  auth: AuthorizationToken;
  repo: string;
}>();
const stateSelection: Ref<"all" | "open" | "closed"> = ref("all");

const params: ComputedRef<IssueListIssuesParams> = computed(() => {
  return {
    state: stateSelection.value,
    type: "issues",
    sort: "oldest",
  };
});
const newItem = ref("");
const { data: repoData } = useRepoGet(props.auth.username, props.repo);
const { data } = useIssueListIssues(props.auth.username, props.repo, params, {
  query: { placeholderData: (prev) => prev },
});
const openCount = computed(
  (): number | undefined => repoData.value?.data.open_issues_count,
);

const mutateOptions = { mutation: { onSuccess: invalidateQueries } };
const { mutate: createIssue } = useIssueCreateIssue(mutateOptions);
const { mutate: mutateIssue } = useIssueEditIssue(mutateOptions);

async function changeIssue(index: number, newData: EditIssueOption) {
  mutateIssue({
    owner: props.auth.username,
    repo: props.repo,
    index: index,
    data: newData,
  });
  await invalidateQueries();
}
async function createNewItem() {
  if (newItem.value.trim()) {
    createIssue({
      owner: props.auth.username,
      repo: props.repo,
      data: { title: newItem.value },
    });
    await invalidateQueries();
  }
}
const queryClient = useQueryClient();
async function invalidateQueries() {
  await queryClient.invalidateQueries({});
}
</script>
